import type { ReactNode } from 'react'

interface PageHeaderProps {
  /** Dynamic content title — selected entity. NEVER repeat the nav label. */
  title: string
  /** One-line context: ID, parent, last-updated. */
  subtitle?: ReactNode
  /** Render only when depth ≥ 2 (excludes current title). */
  breadcrumb?: string[]
  /** Right-aligned action buttons. */
  actions?: ReactNode
  /** Inline pills next to the title. */
  badges?: ReactNode
  /** Below 640 px the actions sit under the title instead of squeezing it (default: beside it, as before). */
  stackActionsOnPhone?: boolean
}

/**
 * Single source of truth for page headers. See DESIGN.md § 9 + § 14.
 * Renders as a Liquid Glass band framing the page H1 — chrome layer, not data.
 */
export function PageHeader({ title, subtitle, breadcrumb, actions, badges, stackActionsOnPhone = false }: PageHeaderProps) {
  const showCrumb = breadcrumb && breadcrumb.length >= 2
  return (
    <header className="liquid-glass rounded-3xl px-5 py-4 mb-6">
      {showCrumb && (
        <nav className="flex items-center gap-1.5 text-[11px] text-on-surface-variant mb-2" aria-label="Breadcrumb">
          {breadcrumb!.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-on-surface-variant/40">/</span>}
              <span className={i === breadcrumb!.length - 1 ? 'text-on-surface font-medium' : ''}>{c}</span>
            </span>
          ))}
        </nav>
      )}
      <div className={`flex items-start justify-between gap-6${stackActionsOnPhone ? ' max-sm:flex-col max-sm:gap-3' : ''}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="font-headline font-bold text-2xl text-on-surface tracking-tight truncate max-w-full" title={title}>{title}</h1>
            {badges}
          </div>
          {subtitle && <p className="text-sm text-on-surface-variant mt-1">{subtitle}</p>}
        </div>
        {actions && <div className={`flex items-center gap-2 flex-shrink-0${stackActionsOnPhone ? ' max-sm:flex-wrap' : ''}`}>{actions}</div>}
      </div>
    </header>
  )
}
